import React, { useState, useEffect } from "react";
import "../css/productdetail.css";
import "../css/admin.css";

const generateTimeSlots = () => {
  const slots = [];
  const startHour = 9; // 9 AM
  const endHour = 17; // 5 PM

  for (let i = startHour; i <= endHour; i++) {
    const hour = i.toString().padStart(2, "0");
    slots.push(`${hour}:00`);
    // Removed the :30 minute push here to enforce strictly hourly slots
  }
  return slots;
};

const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminToken, setAdminToken] = useState("");
  const [inputUsername, setInputUsername] = useState("");
  const [inputPassword, setInputPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // NEW: State for same-day order pausing
  const [isSameDayPaused, setIsSameDayPaused] = useState(false);

  const [delayModal, setDelayModal] = useState({
    isOpen: false,
    orderId: null,
    newDate: "",
    newTime: "",
  });

  const today = new Date().toISOString().split("T")[0];

  const getAvailableTimeSlots = (selectedDate) => {
    const allSlots = generateTimeSlots();

    if (!selectedDate || selectedDate !== today) {
      return allSlots;
    }

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    return allSlots.filter((slot) => {
      const [slotHour, slotMinute] = slot.split(":").map(Number);
      if (slotHour > currentHour) return true;
      if (slotHour === currentHour && slotMinute > currentMinute) return true;
      return false;
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        "https://www.lovelycakes.shop/api/admin/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: inputUsername,
            password: inputPassword,
          }),
        },
      );

      if (response.ok) {
        setIsLoggedIn(true);
        setAdminToken(inputPassword);
      } else {
        alert("Incorrect username or password.");
      }
    } catch (error) {
      console.error("Login failed", error);
      alert("Network error during login.");
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;

    const fetchData = async () => {
      try {
        // Fetch Orders
        const ordersRes = await fetch(
          "https://www.lovelycakes.shop/api/admin/orders",
          {
            headers: { "x-admin-password": adminToken },
          },
        );

        if (ordersRes.ok) {
          const data = await ordersRes.json();
          setOrders(data);
        } else {
          setIsLoggedIn(false);
          return;
        }

        // Fetch Global Settings
        const settingsRes = await fetch(
          "https://www.lovelycakes.shop/api/admin/settings",
          {
            headers: { "x-admin-password": adminToken },
          },
        );

        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          setIsSameDayPaused(settingsData.isSameDayPaused);
        }
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isLoggedIn, adminToken]);

  // NEW: Function to toggle same-day status
  const handleToggleSameDay = async () => {
    const newStatus = !isSameDayPaused;
    setIsSameDayPaused(newStatus); // Optimistic UI update

    try {
      const response = await fetch(
        "https://www.lovelycakes.shop/api/admin/settings/sameday",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-admin-password": adminToken,
          },
          body: JSON.stringify({ isSameDayPaused: newStatus }),
        },
      );

      if (!response.ok) {
        setIsSameDayPaused(!newStatus);
        alert("Failed to update same-day order settings.");
      }
    } catch (error) {
      console.error("Error toggling same-day orders", error);
      setIsSameDayPaused(!newStatus);
      alert("Network error. Setting not updated.");
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(
        `https://www.lovelycakes.shop/api/admin/orders/${orderId}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-admin-password": adminToken,
          },
          body: JSON.stringify({ status: newStatus }),
        },
      );

      if (response.ok) {
        setOrders(
          orders.map((order) =>
            order.id === orderId ? { ...order, status: newStatus } : order,
          ),
        );
      } else {
        alert("Failed to update status.");
      }
    } catch (error) {
      console.error("Error updating status", error);
    }
  };

  const handleDelaySubmit = async () => {
    const { orderId, newDate, newTime } = delayModal;
    if (!newDate || !newTime)
      return alert("Please select both a new date and a new time.");

    const selectedDateTime = new Date(`${newDate}T${newTime}`);
    if (selectedDateTime < new Date()) {
      return alert(
        "You cannot schedule an order in the past. Please update the time.",
      );
    }

    try {
      const response = await fetch(
        `https://www.lovelycakes.shop/api/admin/orders/${orderId}/delay`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-admin-password": adminToken,
          },
          body: JSON.stringify({ newDate, newTime }),
        },
      );

      if (response.ok) {
        setOrders(
          orders.map((order) => {
            if (order.id === orderId) {
              return {
                ...order,
                target_date: newDate,
                target_time: newTime,
                status: "delayed",
              };
            }
            return order;
          }),
        );
        setDelayModal({
          isOpen: false,
          orderId: null,
          newDate: "",
          newTime: "",
        });
      } else {
        alert("Failed to delay order.");
      }
    } catch (error) {
      console.error("Error delaying order", error);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "paid":
        return "status-paid";
      case "completed":
        return "status-completed";
      case "ready_for_collection":
        return "status-paid";
      case "cancelled":
        return "status-cancelled";
      case "delayed":
        return "status-delayed";
      default:
        return "status-pending";
    }
  };

  if (!isLoggedIn) {
    return (
      <div
        className="admin-wrapper"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <form
          onSubmit={handleLogin}
          style={{
            background: "#fff",
            padding: "2rem",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            width: "100%",
            maxWidth: "400px",
          }}
        >
          <h2
            style={{
              textAlign: "center",
              marginBottom: "1.5rem",
              fontFamily: "var(--font-heading)",
            }}
          >
            Admin Access
          </h2>
          <div className="input-group" style={{ marginBottom: "1rem" }}>
            <label>Username</label>
            <input
              type="text"
              value={inputUsername}
              onChange={(e) => setInputUsername(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "0.8rem",
                border: "1px solid #ccc",
              }}
            />
          </div>
          <div className="input-group" style={{ marginBottom: "1.5rem" }}>
            <label>Password</label>
            <input
              type="password"
              value={inputPassword}
              onChange={(e) => setInputPassword(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "0.8rem",
                border: "1px solid #ccc",
              }}
            />
          </div>
          <button
            type="submit"
            className="luxe-btn primary-luxe"
            style={{ width: "100%" }}
          >
            Log In
          </button>
        </form>
      </div>
    );
  }

  if (isLoading)
    return <div className="admin-message">Loading Bakery Data...</div>;

  const visibleOrders = orders.filter((order) => order.status !== "collected");

  return (
    <div className="admin-wrapper">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h1 className="admin-header">Admin Dashboard</h1>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            onClick={handleToggleSameDay}
            className="luxe-btn"
            style={{
              padding: "8px 16px",
              backgroundColor: isSameDayPaused ? "#4CAF50" : "#f44336",
              color: "#fff",
              border: "none",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {isSameDayPaused
              ? "▶ Resume Same-Day Orders"
              : "⏸ Pause Same-Day Orders"}
          </button>
          <button
            onClick={() => {
              setIsLoggedIn(false);
              setAdminToken("");
              setInputPassword("");
            }}
            className="luxe-btn secondary-luxe"
            style={{ padding: "8px 16px" }}
          >
            Logout
          </button>
        </div>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date & Time Needed</th>
              <th>Customer Details</th>
              <th>Order Details</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleOrders.map((order) => {
              return (
                <tr key={order.id}>
                  <td style={{ verticalAlign: "top" }}>
                    <div
                      className="order-date"
                      style={{ fontWeight: "bold", fontSize: "1.05rem" }}
                    >
                      {new Date(order.target_date).toLocaleDateString("en-GB", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </div>
                    {order.target_time && (
                      <div
                        className="order-time"
                        style={{
                          marginTop: "0.5rem",
                          color: "#d32f2f",
                          fontWeight: "bold",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        ⏱️ {order.target_time}
                      </div>
                    )}
                  </td>
                  <td style={{ verticalAlign: "top" }}>
                    <div
                      className="customer-name"
                      style={{ fontWeight: "bold" }}
                    >
                      {order.customer_name}
                    </div>
                    <div className="customer-phone">{order.customer_phone}</div>
                    <div
                      className="customer-email"
                      style={{ fontSize: "0.85rem", color: "#555" }}
                    >
                      <a href={`mailto:${order.customer_email}`}>
                        {order.customer_email}
                      </a>
                    </div>
                  </td>
                  <td style={{ verticalAlign: "top" }}>
                    <div
                      className="cake-name"
                      style={{
                        fontWeight: "bold",
                        color: "var(--primary-color)",
                      }}
                    >
                      {order.product_name}
                    </div>
                    <div
                      className="cake-meta"
                      style={{ marginBottom: "0.5rem" }}
                    >
                      Size: {order.size} | Base:{" "}
                      {order.base_flavor || "Vanilla"} | £{order.price}
                    </div>
                    {order.notes && (
                      <div
                        className="cake-notes"
                        style={{
                          fontSize: "0.85rem",
                          backgroundColor: "#f9f9f9",
                          padding: "8px",
                          borderLeft: "3px solid #ccc",
                          fontStyle: "italic",
                        }}
                      >
                        <strong>Requests:</strong> {order.notes}
                      </div>
                    )}
                  </td>
                  <td style={{ verticalAlign: "top" }}>
                    <span
                      className={`status-badge ${getStatusClass(order.status)}`}
                    >
                      {order.status.replace(/_/g, " ").toUpperCase()}
                    </span>
                  </td>
                  <td
                    className="admin-actions"
                    style={{
                      verticalAlign: "top",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {order.status !== "ready_for_collection" &&
                      order.status !== "completed" &&
                      order.status !== "cancelled" && (
                        <button
                          onClick={() =>
                            updateStatus(order.id, "ready_for_collection")
                          }
                          className="luxe-btn primary-luxe"
                          style={{
                            padding: "5px 10px",
                            fontSize: "0.8rem",
                            width: "100%",
                          }}
                        >
                          Ready for Collection
                        </button>
                      )}
                    {order.status === "ready_for_collection" && (
                      <button
                        onClick={() => updateStatus(order.id, "completed")}
                        className="luxe-btn primary-luxe"
                        style={{
                          padding: "5px 10px",
                          fontSize: "0.8rem",
                          width: "100%",
                          backgroundColor: "#4CAF50",
                          borderColor: "#4CAF50",
                        }}
                      >
                        Complete Order
                      </button>
                    )}
                    {order.status === "completed" && (
                      <div
                        style={{
                          padding: "5px 10px",
                          fontSize: "0.8rem",
                          width: "100%",
                          backgroundColor: "#e8f5e9",
                          color: "#2e7d32",
                          textAlign: "center",
                          border: "1px solid #4CAF50",
                          borderRadius: "4px",
                          fontWeight: "bold",
                        }}
                      >
                        ✓ Completed
                      </div>
                    )}
                    {order.status !== "completed" &&
                      order.status !== "cancelled" && (
                        <>
                          <button
                            onClick={() =>
                              setDelayModal({
                                isOpen: true,
                                orderId: order.id,
                                newDate: order.target_date
                                  ? order.target_date.split("T")[0]
                                  : "",
                                newTime: order.target_time || "",
                              })
                            }
                            className="luxe-btn secondary-luxe"
                            style={{
                              padding: "5px 10px",
                              fontSize: "0.8rem",
                              width: "100%",
                            }}
                          >
                            Delay Order
                          </button>
                          <button
                            onClick={() => updateStatus(order.id, "cancelled")}
                            className="luxe-btn secondary-luxe"
                            style={{
                              padding: "5px 10px",
                              fontSize: "0.8rem",
                              width: "100%",
                              backgroundColor: "#f44336",
                              color: "#fff",
                              border: "none",
                            }}
                          >
                            Cancel Order
                          </button>
                        </>
                      )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {visibleOrders.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              fontSize: "1.2rem",
              color: "#666",
            }}
          >
            No orders found. Time to bake something new!
          </div>
        )}
      </div>

      {/* DELAY MODAL */}
      {delayModal.isOpen && (
        <div
          className="popup-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "2rem",
              borderRadius: "8px",
              width: "400px",
              maxWidth: "90%",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: "1.5rem" }}>
              Reschedule Order
            </h3>
            <div className="input-group" style={{ marginBottom: "1rem" }}>
              <label>New Date</label>
              <input
                type="date"
                min={today}
                value={delayModal.newDate}
                onChange={(e) =>
                  setDelayModal({
                    ...delayModal,
                    newDate: e.target.value,
                    newTime: "",
                  })
                }
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: "1px solid #ccc",
                }}
              />
            </div>
            <div className="input-group" style={{ marginBottom: "1.5rem" }}>
              <label>New Time</label>
              <select
                value={delayModal.newTime}
                onChange={(e) =>
                  setDelayModal({ ...delayModal, newTime: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: "1px solid #ccc",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                }}
                disabled={!delayModal.newDate}
              >
                <option value="" disabled>
                  {delayModal.newDate
                    ? "Select a time slot"
                    : "Select a date first"}
                </option>
                {getAvailableTimeSlots(delayModal.newDate).map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={handleDelaySubmit}
                className="luxe-btn primary-luxe"
                style={{ flex: 1, padding: "0.8rem" }}
              >
                Confirm Delay
              </button>
              <button
                onClick={() =>
                  setDelayModal({
                    isOpen: false,
                    orderId: null,
                    newDate: "",
                    newTime: "",
                  })
                }
                className="luxe-btn secondary-luxe"
                style={{
                  flex: 1,
                  padding: "0.8rem",
                  backgroundColor: "#f5f5f5",
                  color: "#333",
                  border: "1px solid #ccc",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
